import React, {
    ReactElement, ReactNode, useEffect, useMemo, useState,
} from "react";
import classNames from "classnames";
import {
    Form as ApiForm,
    FormField as ApiFormField,
    CustomField,
    CustomFieldValue,
    FieldTypedReplaceableValue,
    FormFieldDescriptor, FormFieldType,
    FormFieldVisibility, Ticket, TicketInput,
    useGetConditionalFormQuery,
    useGetFormsQuery,
} from "../../__generated__/api";
import { useTheme } from "../../contexts/Theme";
import EditableControl, { ControlType } from "../editable-text/EditableText";
import FormField from "../form-field/FormField";
import { HtmlRenderer } from "../html-render/HtmlRenderer";
import { LoadingIcon } from "../loading/Loading";
import Styles from "./Form.module.scss";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import { SYSTEM_FIELDS } from "../workflow/WorkflowFields";

export enum FormDisplayUserType {
    AGENT,
    REQUESTOR
}

export enum FormCreateType {
    CREATABLE = "CREATABLE",
    READONLY = "READONLY"
}
interface FormProps {
    variant?: "horizontal" | "vertical",
    className?: string;
    descriptionMessage?: ReactElement | string;
    onChange?: (ticket: Partial<TicketInput>) => void;
    onChangeRequestType?: (old: ApiForm | undefined, newForm: ApiForm) => void;
    ticket?: Ticket | undefined;
    isEditable: boolean;
    canSelectRequestType?: boolean;
    shouldValidateAutomations?: boolean;
    selectDefaultRequestType?: boolean;
    displayUserType?: FormDisplayUserType;
    isEditing?: boolean;
    shouldShowDescriptions?: boolean;
    shouldApplyRequired?: boolean;
    shouldValidateAutomation?: boolean;
    isFormField?: boolean;
    formId?: string;
    setCreateType?: (type: FormCreateType) => void;
    onEmptyMessage?: ReactElement | string;
    skipFields?: string[];
    shouldFilterAgentRequired?: boolean;
    editIcon?: ReactNode;
    groupClassName?: string;
    metricPathPrefix?: string[];
}

export const SUBJECT_ID = "urn:fq:form-field:subject";
export const DESCRIPTION_ID = "urn:fq:form-field:description";
export const PRIORITY_ID = "urn:fq:form-field:priority";
export const REQUESTOR_ID = "urn:fq:form-field:requestorId";
export const TAGS_ID = "urn:fq:form-field:tags";
export const SEVERITY_ID = "urn:fq:form-field:severity";

const HANDLED_TYPES = new Set([SUBJECT_ID, DESCRIPTION_ID]);
const DEFAULT_FIELDS: ApiFormField[] = [
    {
        id: SUBJECT_ID,
        name: "Subject",
        description: "The ticket subject",
        isSystemField: true,
        type: FormFieldType.Text,
        visibility: FormFieldVisibility.Reusable,
    },
    {
        id: DESCRIPTION_ID,
        name: "Description",
        description: "The ticket description",
        isSystemField: true,
        type: FormFieldType.LongText,
        visibility: FormFieldVisibility.Reusable,
    },
];

function isValueEmpty(value: CustomFieldValue | undefined | null) {
    return value == null || (
        value.boolean == null
        && value.date == null
        && (value.files == null || value.files.isEmpty)
        && value.number == null
        && (value.options == null || value.options.isEmpty)
        && value.string == null
    );
}

export default function Form({
    isEditable, onChange, ticket, className, canSelectRequestType, displayUserType, isEditing,
    setCreateType, selectDefaultRequestType, shouldShowDescriptions, shouldApplyRequired, isFormField,
    formId: inputFormId, shouldValidateAutomations, onChangeRequestType, onEmptyMessage, skipFields,
    shouldValidateAutomation, shouldFilterAgentRequired, descriptionMessage, variant, editIcon, groupClassName,
    metricPathPrefix,
}: FormProps): ReactElement | null {
    // eslint-disable-next-line prefer-const
    let { data: formData, loading: isLoading } = useGetFormsQuery({
        variables: {
            needforms: inputFormId == null || canSelectRequestType === true,
        },
    });

    const theme = useTheme();
    const { data: form, loading: isFormLoading, refetch: refetchForm } = useGetConditionalFormQuery({
        skip: inputFormId == null || !shouldValidateAutomation,
        variables: {
            formId: inputFormId,
            shouldValidateAutomations: shouldValidateAutomations ?? false,
        },
    });

    const [formId, setFormId] = useState<string | undefined>();

    useEffect(() => {
        if (ticket?.formId) {
            setFormId(ticket?.formId);
        } else if (!formId && formData?.forms?.isEmpty) {
            setFormId(formData?.forms.firstElement?.id);
        }
    }, [ticket?.formId, formData]);

    useEffect(() => {
        if (inputFormId) {
            setFormId(inputFormId);
        }
    }, [inputFormId]);

    const skipped = useMemo(() => new Set(skipFields ?? []), [skipFields]);

    const {
        currentForm, fieldOverrides, formFields, formFieldMap,
    } = useMemo(() => {
        const textField: keyof FormFieldDescriptor = displayUserType === FormDisplayUserType.AGENT
            ? "agentText"
            : "requestorText";

        const allFields = formData?.formFields.toIdMap("id") ?? {};
        const currForm = form?.form ?? formData?.forms?.find(current => current.id === formId);
        const overrides = currForm?.fields?.filterNull().toIdMap(row => row?.fieldId ?? "") ?? {};
        let fields = currForm?.fields?.filterNull().mapFilter(field => (
            field[textField] ? { text: field[textField] } : allFields[field.fieldId?.toString()]))
            ?? DEFAULT_FIELDS;
        const map = ticket?.customFields?.toIdMap("formFieldId", value => ({
            formFieldId: value.formFieldId,
            displayValue: value.displayValue,
            formField: value.formField,
            value: value.value ? value.value : {
                ...value,
            },
            type: value.type,
        } as CustomField)) ?? {};
        map[SUBJECT_ID] ??= {
            formFieldId: SUBJECT_ID,
            value: {
                formFieldId: SUBJECT_ID,
                string: ticket?.subject,
            },
        };
        map[DESCRIPTION_ID] ??= {
            formFieldId: DESCRIPTION_ID,
            value: {
                formFieldId: DESCRIPTION_ID,
                string: ticket?.description,
            },
        };
        map[PRIORITY_ID] ??= {
            formFieldId: PRIORITY_ID,
            value: {
                formFieldId: PRIORITY_ID,
                string: ticket?.priority,
            },
        };
        map[REQUESTOR_ID] ??= {
            formFieldId: REQUESTOR_ID,
            value: {
                formFieldId: REQUESTOR_ID,
                string: ticket?.requestorId,
            },
        };
        map[TAGS_ID] ??= {
            formFieldId: TAGS_ID,
            value: {
                formFieldId: TAGS_ID,
                options: ticket?.tags,
            },
        };
        map[SEVERITY_ID] ??= {
            formFieldId: SEVERITY_ID,
            value: {
                formFieldId: SEVERITY_ID,
                number: ticket?.severityIndex,
            },
        };

        if (shouldFilterAgentRequired) {
            fields = fields.filter(field => {
                const fieldId = (field as ApiFormField).id;
                return overrides[fieldId]?.isRequiredForAgent && isValueEmpty(map[fieldId]?.value);
            });
        }

        return {
            textField,
            allFields,
            currentForm: currForm,
            fieldOverrides: overrides,
            formFields: fields,
            formFieldMap: map,
        };
    }, [formData, form, formId, ticket]);

    useEffect(() => {
        const canCreate = formFields.some(field => (field as {text: string}).text == null);
        setCreateType?.(canCreate ? FormCreateType.CREATABLE : FormCreateType.READONLY);
    }, [formId]);

    const changeRequestType = (newId: string) => {
        const oldCurrent = currentForm;
        if (currentForm?.id === newId) {
            return;
        }
        setFormId(newId);

        // eslint-disable-next-line eqeqeq
        if (newId != ticket?.formId) {
            onChange?.({
                formId: newId,
            });
        }
        if (onChangeRequestType) {
            (async () => {
                const { data } = await refetchForm({
                    formId: newId,
                    shouldValidateAutomations: shouldValidateAutomations ?? false,
                });
                onChangeRequestType(oldCurrent as ApiForm, data.form as ApiForm);
            })();
        }

        if (metricPathPrefix) {
            LoggingHandler.instance.metric([...metricPathPrefix, "change", "request-type"]);
        }
    };

    useEffect(() => {
        if (selectDefaultRequestType && !formId && formData?.forms?.isNotEmpty) {
            const firstForm = formData?.forms?.firstElement;
            changeRequestType(firstForm.id);
        }
    }, [formData?.forms]);

    if ((selectDefaultRequestType && isLoading) || isFormLoading) {
        return (
            <div className="d-flex justify-content-center">
                <LoadingIcon />
            </div>
        );
    }

    if (!canSelectRequestType && formFields.isEmpty && !onEmptyMessage) {
        return null;
    }
    const rowClassName = theme.isV3 ? classNames("form-group", Styles["details-row"], groupClassName) : "mb-3";
    const titleClassName = theme.isV3 ? classNames("form-label", Styles["details-row-title"]) : "text-gray-500";
    const valueClassName = theme.isV3 ? Styles["details-row-value"] : "";
    const descriptionClassName = theme.isV3 ? classNames("form-text", Styles["details-row-description"]) : "";

    const inputClassName = theme.isV3 ? "form-control form-control-sm" : undefined;

    const handleChange = metricPathPrefix
        ? (changed: Partial<TicketInput>, fieldId: string) => {
            onChange?.(changed);
            LoggingHandler.instance.metric([...metricPathPrefix, "change", fieldId]);
        }
        : onChange;

    return (
        <div className={classNames(className, Styles[`form-${variant}`])}>
            {descriptionMessage}
            {(canSelectRequestType && (formData?.forms?.isNotEmpty || currentForm)) && (
                <div className={rowClassName}>
                    <small className={titleClassName}>
                        Request Type
                    </small>
                    <div className={valueClassName}>
                        {/* <RequestTypes
                            onSelected={value => setFormId(value)}
                            value={formId}
                        /> */}
                        <EditableControl
                            isReadOnly={!isEditable}
                            type={ControlType.SELECT}
                            value={formId}
                            isEditing={isEditing}
                            isRequired
                            shouldFocus
                            inputClassName={inputClassName}
                            placeholder="Request Type"
                            editIcon={editIcon}
                            onChange={changed => changeRequestType(changed as string)}
                            options={formData?.forms?.map(opt => ({
                                label: opt.name,
                                value: opt.id,
                                data: opt,
                            }))}
                        >
                            {currentForm ? <span>{currentForm.name}</span> : undefined}
                        </EditableControl>
                    </div>

                </div>
            )}

            {formFields.isEmpty && onEmptyMessage}

            {formFields.filter(field => !skipped.has((field as {id: string}).id)).map(rawFormField => {
                const withText = rawFormField as {text: string};
                if (withText.text) {
                    return (
                        <div className="mb-3 alert-light p-3 rounded" key={withText.text}>
                            <HtmlRenderer html={withText.text} />
                        </div>
                    );
                }
                const formField = rawFormField as ApiFormField;
                const valueField = formFieldMap[formField.id];
                const override = fieldOverrides?.[formField.id];
                let isRequired = override?.isRequired ?? formField.isRequired ?? false;
                if (shouldFilterAgentRequired) {
                    isRequired = override?.isRequiredForAgent ?? isRequired;
                }

                let fieldClass = "";
                if (formField.id === DESCRIPTION_ID) {
                    fieldClass = Styles["form-input-description"];
                } else if (formField.id === SUBJECT_ID) {
                    fieldClass = Styles["form-input-subject"];
                } else {
                    fieldClass = Styles[`form-input-${formField.type.toLowerCase().replace(/_/g, "-")}`];
                }

                return (
                    <div className={rowClassName} key={formField.id}>
                        <small className={titleClassName} title={override?.description ?? formField?.description as string}>
                            {override?.name ?? formField?.name}
                            {" "}
                            {shouldApplyRequired
                                && !isRequired
                                && <span className={classNames(Styles.optional, "form-label-optional")}>(Optional)</span>}
                        </small>
                        <div className={classNames(valueClassName, fieldClass)}>
                            {formField.id === DESCRIPTION_ID && (
                                <EditableControl
                                    isReadOnly={!isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    editIcon={editIcon}
                                    inputClassName={inputClassName}
                                    value={ticket?.description?.trim()
                                        ?? formField.defaultValue?.string
                                        ?? override?.defaultValue?.value.string as string | undefined}
                                    placeholder={override?.placeholder
                                            ?? formField?.placeholder
                                             ?? "Enter Description"}
                                    type={ControlType.LONG_TEXT}
                                    isEditing={isEditing}
                                    onChange={changed => handleChange?.({ description: changed as string }, DESCRIPTION_ID)}
                                />
                            )}
                            {formField.id === SUBJECT_ID && (
                                <EditableControl
                                    isReadOnly={!isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    editIcon={editIcon}
                                    inputClassName={inputClassName}
                                    value={ticket?.subject?.trim()
                                            ?? formField.defaultValue?.string
                                            ?? override?.defaultValue?.value.string as string | undefined}
                                    placeholder={override?.placeholder
                                            ?? formField?.placeholder
                                             ?? "Enter Subject"}
                                    isEditing={isEditing}
                                    onChange={changed => handleChange?.({ subject: changed as string }, SUBJECT_ID)}
                                />
                            )}
                            {!HANDLED_TYPES.has(formField.id) && (
                                <FormField
                                    isEditable={isEditable}
                                    isRequired={isRequired}
                                    isFormField={isFormField}
                                    shouldFocus={false}
                                    editIcon={editIcon}
                                    inputClassName={inputClassName}
                                    formField={{
                                        ...formField,
                                        placeholder: override?.placeholder ?? formField?.placeholder,
                                    }}
                                    onChange={change => handleChange?.(change, formField.isSystemField
                                        ? formField.id
                                        : "urn:fq:form-field:custom-field")}
                                    isEditing={isEditing}
                                    value={valueField}
                                    defaultValue={override?.defaultValue as FieldTypedReplaceableValue}
                                />
                            )}

                        </div>
                        {shouldShowDescriptions && (
                            <small className={descriptionClassName}>
                                {formField.description}
                            </small>
                        )}

                    </div>

                );
            })}
        </div>
    );
}

Form.defaultProps = {
    onChange: () => {
        // nothing
    },
    ticket: undefined,
    canSelectRequestType: false,
    selectDefaultRequestType: false,
    className: "",
    isEditing: undefined,
    displayUserType: FormDisplayUserType.AGENT,
    setCreateType: undefined,
    shouldShowDescriptions: false,
    shouldApplyRequired: false,
    isFormField: undefined,
    formId: undefined,
    shouldValidateAutomations: false,
    onEmptyMessage: "",
    skipFields: [],
    shouldValidateAutomation: false,
    shouldFilterAgentRequired: false,
    descriptionMessage: undefined,
    variant: "horizontal",
    editIcon: undefined,
    groupClassName: undefined,
    metricPathPrefix: undefined,
    onChangeRequestType: () => {
        // do nothing
    },
};
